import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, X, PawPrint, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, photoUrl } from "../../lib/api";
import { priceLabel } from "../../lib/constants";
import { getRecent, clearRecent } from "../../lib/recentlyViewed";
import { favSavedToast } from "../../lib/favorite";
import { useAuth } from "../../context/AuthContext";
import { CategoryCarousel } from "../CategoryCarousel";
import { useHomeFeed } from "../../lib/siteContent";

const perViewFor = (desktop) => () => (window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 3 : desktop);
export const PER = { 4: perViewFor(4), 6: perViewFor(6) };

const priceText = (l, t, lang) => (l.category === "iso" ? (lang === "fr" ? "Recherché" : "Wanted") : l.reunited_at ? t("lfr_back_home") : l.lf_type ? t(l.lf_type === "lost" ? "lf_lost" : "lf_found")
  : l.price_type === "free" ? t("ld_free") : (l.price_type === "contact" || l.price == null) ? t("ld_contact_price") : priceLabel(l));

const FavHeart = ({ l, lang, onChange, prefix }) => {
  const { user, setAuthOpen } = useAuth();
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) return setAuthOpen(true);
    try {
      l.is_favorited ? await api.delete(`/favorites/${l.id}`) : await api.post(`/favorites/${l.id}`);
      if (!l.is_favorited) favSavedToast(l, lang);
      onChange(!l.is_favorited);
    } catch { toast.error(lang === "fr" ? "Impossible de mettre à jour" : "Could not update favourite"); }
  };
  return (
    <button onClick={toggle} data-testid={`${prefix}-fav-${l.id}`} aria-label="Save" aria-pressed={!!l.is_favorited}
      className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform">
      <Heart size={14} className={l.is_favorited ? "fill-[#F5551E] text-[#F5551E]" : "text-slate-500"} />
    </button>
  );
};

export const RecentTile = ({ l, t, lang, onFav, dup, prefix = "recent" }) => (
  <Link to={`/listing/${l.id}`} data-testid={dup ? `${prefix}-item-dup-${l.id}` : `${prefix}-item-${l.id}`} tabIndex={dup ? -1 : undefined}
    className="group relative block w-full bg-white rounded-xl border border-slate-200/80 overflow-hidden hover:border-[#F5551E]/50 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
    <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
      {l.photos?.[0]
        ? <img src={photoUrl(l.photos[0])} alt={l.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
        : <div className="h-full w-full flex items-center justify-center text-slate-300"><PawPrint size={24} /></div>}
      {l.category === "iso" && <span className="absolute left-1.5 bottom-1.5 bg-[#FFC233] text-[#12234A] text-[10px] font-black tracking-widest px-1.5 py-0.5 rounded" data-testid={`recent-iso-${l.id}`}>ISO</span>}
    </div>
    {!l.lf_type && <FavHeart l={l} lang={lang} onChange={onFav} prefix={prefix} />}
    <div className="p-2.5">
      <p className="text-xs font-semibold text-[#12234A] truncate">{l.title}</p>
      <p className="text-xs font-bold text-[#F5551E] mt-0.5 truncate">{priceText(l, t, lang)}</p>
    </div>
  </Link>
);

export const RecentlyViewed = ({ t, lang, className = "mb-6", testid = "recently-viewed-strip", desktop = 4 }) => {
  const [items, setItems] = useState([]);
  const feed = useHomeFeed();
  useEffect(() => {
    const ids = getRecent();
    if (ids.length) api.get(`/listings-brief?ids=${ids.join(",")}`).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  if (!items.length) return null;
  const clear = () => { clearRecent(); setItems([]); };
  const setFav = (id, v) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, is_favorited: v } : x)));
  return (
    <section className={className} data-testid={testid}>
      <div className="flex items-center justify-between mb-2.5">
        <h2 className="font-display font-bold text-[#12234A] text-base flex items-center gap-1.5"><History size={16} className="text-[#F5551E]" /> {feed[`recent_title_${lang}`] || (lang === "fr" ? "Vus récemment" : "Recently viewed")}</h2>
        <button onClick={clear} data-testid="recently-viewed-clear" className="text-xs text-slate-400 hover:text-[#F5551E] inline-flex items-center gap-1 transition-colors"><X size={12} /> {lang === "fr" ? "Effacer" : "Clear"}</button>
      </div>
      <CategoryCarousel items={items} perView={PER[desktop] || PER[4]} testid="recent-carousel" label={lang === "fr" ? "annonces" : "ads"}
        renderItem={(l, dup) => <RecentTile l={l} t={t} lang={lang} dup={dup} onFav={(v) => setFav(l.id, v)} />} />
    </section>
  );
};
